import { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Search, Star, Save, Coins, Shuffle, RotateCcw } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import DriverCard from "@/components/DriverCard";
import AdBanner from "@/components/AdBanner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const BUDGET = 120;
const TEAM_SIZE = 18;
const DEFAULT_LIMIT = 1;

export default function Market() {
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [gruppo, setGruppo] = useState("");
  const [sort, setSort] = useState("");
  const [selected, setSelected] = useState([]);
  const [captainId, setCaptainId] = useState(null);
  const [nomeSquadra, setNomeSquadra] = useState("");
  const [saving, setSaving] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);

  const { data: pilots = [] } = useQuery({
    queryKey: ["pilots"],
    queryFn: async () => (await api.get("/pilots")).data,
  });

  const { data: gruppi = [] } = useQuery({
    queryKey: ["gruppi"],
    queryFn: async () => (await api.get("/gruppi")).data,
  });

  const { setUser } = useAuth();

  const { data: teamData } = useQuery({
    queryKey: ["my-team"],
    queryFn: async () => (await api.get("/team")).data,
  });

  const { data: settings } = useQuery({
    queryKey: ["settings-public"],
    queryFn: async () => (await api.get("/settings/public")).data,
  });

  useEffect(() => {
    if (teamData?.team) {
      setSelected(teamData.team.pilot_ids || []);
      setCaptainId(teamData.team.captain_id || null);
      setNomeSquadra(teamData.team.nome_squadra || "");
    }
  }, [teamData]);

  const byId = useMemo(() => Object.fromEntries(pilots.map((p) => [p.id, p])), [pilots]);
  const classLimits = settings?.class_limits || {};
  const limitFor = (g) => classLimits[g] ?? DEFAULT_LIMIT;

  const visiblePilots = useMemo(() => {
    let list = pilots;
    if (gruppo) list = list.filter((p) => p.gruppo === gruppo);
    if (q) {
      const ql = q.toLowerCase();
      list = list.filter((p) =>
        `${p.nome} ${p.cognome} ${p.vettura}`.toLowerCase().includes(ql)
      );
    }
    if (sort === "prezzo_asc") return [...list].sort((a, b) => a.prezzo - b.prezzo);
    if (sort === "prezzo_desc") return [...list].sort((a, b) => b.prezzo - a.prezzo);
    if (sort === "punti") return [...list].sort((a, b) => (b.punti_totali || 0) - (a.punti_totali || 0));
    return list;
  }, [pilots, gruppo, q, sort]);

  const classCounts = useMemo(() => {
    const m = {};
    selected.forEach((id) => {
      const g = byId[id]?.gruppo;
      if (g) m[g] = (m[g] || 0) + 1;
    });
    return m;
  }, [selected, byId]);

  const costo = selected.reduce((acc, id) => acc + (byId[id]?.prezzo || 0), 0);
  const residui = BUDGET - costo;
  const overBudget = residui < 0;
  const canSave =
    selected.length === TEAM_SIZE && !overBudget && !!captainId && !saving;

  const race = settings?.race || {};
  const nowTs = Date.now();
  const lockTs = race.lock_at ? new Date(race.lock_at).getTime() : null;
  const locked = !!(lockTs && nowTs > lockTs);
  const cws = race.captain_window_start ? new Date(race.captain_window_start).getTime() : null;
  const cwe = race.captain_window_end ? new Date(race.captain_window_end).getTime() : null;
  const captainWindow = locked && (!cws || nowTs >= cws) && (!cwe || nowTs <= cwe);
  const canSubmit = canSave && (!locked || captainWindow);

  const addPilot = (p) => {
    if (selected.includes(p.id)) return;
    if (selected.length >= TEAM_SIZE) {
      toast.error(`Squadra al completo: massimo ${TEAM_SIZE} piloti`);
      return;
    }
    const lim = limitFor(p.gruppo);
    if ((classCounts[p.gruppo] || 0) >= lim) {
      toast.error(`Hai già selezionato il numero massimo di ${lim} piloti per questa classe (${p.gruppo})`, { id: `limit-${p.gruppo}` });
      return;
    }
    const newSelected = [...selected, p.id];
    setSelected(newSelected);
    if (newSelected.length === 1) setCaptainId(p.id);
    toast.success(`${p.cognome} aggiunto alla scuderia!`, { id: `add-${p.id}` });
  };

  const removePilot = (p) => {
    const newSelected = selected.filter((id) => id !== p.id);
    setSelected(newSelected);
    if (captainId === p.id) setCaptainId(newSelected[0] || null);
    toast(`${p.cognome} rimosso dalla squadra`);
  };

  const setCaptain = (p) => {
    setCaptainId(p.id);
    toast.success(`${p.cognome} nominato Capitano (punti x1.5)`);
  };

  const resetTeam = () => {
    setSelected([]);
    setCaptainId(null);
    setResetOpen(false);
    toast.success("Scuderia azzerata: puoi ripartire da zero!");
  };

  const generateRandomTeam = () => {
    for (let attempt = 0; attempt < 300; attempt++) {
      const shuffled = [...pilots].sort(() => Math.random() - 0.5);
      const sel = [];
      const gc = {};
      let cost = 0;
      for (const p of shuffled) {
        if (sel.length >= TEAM_SIZE) break;
        if (sel.includes(p.id) || (gc[p.gruppo] || 0) >= limitFor(p.gruppo)) continue;
        if (cost + p.prezzo > BUDGET) continue;
        sel.push(p.id);
        gc[p.gruppo] = (gc[p.gruppo] || 0) + 1;
        cost += p.prezzo;
      }
      if (sel.length === TEAM_SIZE) {
        setSelected(sel);
        setCaptainId(sel[Math.floor(Math.random() * sel.length)]);
        toast.success(`Squadra casuale generata: ${TEAM_SIZE} piloti, ${cost} crediti. Puoi modificarla prima di salvare!`);
        return;
      }
    }
    toast.error("Generazione fallita: riprova o componi manualmente");
  };

  const saveTeam = async () => {
    setSaving(true);
    try {
      await api.put("/team", {
        nome_squadra: nomeSquadra || "La mia scuderia",
        pilot_ids: selected,
        captain_id: captainId,
      });
      toast.success("Squadra salvata! In bocca al lupo per la salita.");
      qc.invalidateQueries({ queryKey: ["my-team"] });
      api.get("/auth/me").then((r) => setUser(r.data)).catch(() => {});
    } catch (e) {
      toast.error(errMsg(e, "Salvataggio fallito"));
    } finally {
      setSaving(false);
    }
  };

  const inputCls =
    "rounded-lg bg-[#121214] border border-[#3F3F4C] px-3 py-2 text-sm text-white placeholder:text-gray-600 focus:outline-none focus:border-[#FF1E27] transition-colors font-body-sport";

  return (
    <div data-testid="market-page" className="space-y-6 pb-40">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <h2 className="font-racing font-bold text-xl sm:text-2xl uppercase tracking-wide text-white border-l-4 border-[#FF1E27] pl-3">
            Mercato Piloti
          </h2>
          <p className="text-gray-500 text-xs mt-1 font-body-sport pl-4">
            Componi la tua Griglia di Partenza: {TEAM_SIZE} piloti — 1 per ogni Gruppo — 120 crediti
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {!locked && (
            <>
              <button
                data-testid="random-team-button"
                onClick={generateRandomTeam}
                className="flex items-center gap-2 rounded-lg border border-[#FFB703] text-[#FFB703] hover:bg-[#FFB703]/10 transition-colors px-4 py-2 min-h-[40px] font-body-sport font-bold text-xs tracking-widest uppercase"
              >
                <Shuffle className="w-4 h-4" /> Genera Squadra Casuale
              </button>
              <button
                data-testid="reset-team-button"
                onClick={() => setResetOpen(true)}
                disabled={selected.length === 0}
                className="flex items-center gap-2 rounded-lg border border-[#EF4444] text-[#EF4444] hover:bg-[#EF4444]/10 disabled:opacity-40 transition-colors px-4 py-2 min-h-[40px] font-body-sport font-bold text-xs tracking-widest uppercase"
              >
                <RotateCcw className="w-4 h-4" /> Azzera Scuderia
              </button>
            </>
          )}
          <div className="relative">
            <Search className="w-4 h-4 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              data-testid="market-search-input"
              className={`${inputCls} pl-9 w-full sm:w-52`}
              placeholder="Cerca pilota o vettura"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>
          <select
            data-testid="market-sort-select"
            className={inputCls}
            value={sort}
            onChange={(e) => setSort(e.target.value)}
          >
            <option value="">N° Gara</option>
            <option value="prezzo_asc">Prezzo crescente</option>
            <option value="prezzo_desc">Prezzo decrescente</option>
            <option value="punti">Punti</option>
          </select>
        </div>
      </div>

      <div className="flex gap-1.5 overflow-x-auto pb-1 sm:flex-wrap sm:overflow-visible" data-testid="class-tabs">
        <button
          data-testid="class-tab-all"
          onClick={() => setGruppo("")}
          className={`shrink-0 rounded-lg border px-3 py-2 min-h-[40px] font-body-sport text-[11px] font-bold tracking-wider uppercase transition-colors ${
            !gruppo ? "border-[#FF1E27] bg-[#FF1E27]/15 text-[#FF1E27]" : "border-[#3F3F4C] text-gray-400 hover:text-white"
          }`}
        >
          Tutti
        </button>
        {gruppi.map((g) => {
          const cnt = classCounts[g] || 0;
          const lim = limitFor(g);
          return (
            <button
              key={g}
              data-testid={`class-tab-${g}`}
              onClick={() => setGruppo(gruppo === g ? "" : g)}
              className={`shrink-0 rounded-lg border px-3 py-2 min-h-[40px] font-body-sport text-[11px] font-bold tracking-wider uppercase transition-colors ${
                gruppo === g ? "border-[#FF1E27] bg-[#FF1E27]/15 text-[#FF1E27]" : "border-[#3F3F4C] text-gray-400 hover:text-white"
              }`}
            >
              {g}{" "}
              <span
                data-testid={`class-tab-count-${g}`}
                className={`ml-1 font-time ${cnt >= lim ? "text-[#22C55E]" : "text-gray-500"}`}
              >
                {cnt}/{lim}
              </span>
            </button>
          );
        })}
      </div>


      {locked && (
        <div
          data-testid="team-locked-banner"
          className="rounded-xl border border-[#FFB703]/50 bg-[#FFB703]/10 px-4 py-3 text-sm font-body-sport text-[#FFB703]"
        >
          {captainWindow
            ? `Formazione bloccata: fino all'inizio della Gara puoi solo cambiare il Capitano tra i tuoi ${TEAM_SIZE} piloti.`
            : `Formazione bloccata dalle ore 07:00 del 06/11 (inizio Prove). I ${TEAM_SIZE} piloti non possono più essere sostituiti.`}
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {visiblePilots.map((p, i) => (
          <div key={p.id} className="contents">
            <DriverCard
              pilot={p}
              selected={selected.includes(p.id)}
              isCaptain={captainId === p.id}
              classCount={classCounts[p.gruppo] || 0}
              classLimit={limitFor(p.gruppo)}
              onAdd={!locked ? () => addPilot(p) : undefined}
              onRemove={!locked ? () => removePilot(p) : undefined}
              onCaptain={!locked || captainWindow ? () => setCaptain(p) : undefined}
            />
            {i === 5 && (
              <div className="sm:col-span-2 lg:col-span-3 flex justify-center py-1">
                <AdBanner slot="infeed" />
              </div>
            )}
          </div>
        ))}
      </div>

      <AlertDialog open={resetOpen} onOpenChange={setResetOpen}>
        <AlertDialogContent className="bg-[#1A1A1E] border-[#3F3F4C]" data-testid="reset-team-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-racing uppercase tracking-wide text-white">
              Azzerare la scuderia?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-gray-400 font-body-sport">
              Tutti i piloti selezionati verranno rimossi dalla griglia corrente e potrai ripartire da zero.
              La squadra salvata in precedenza resta valida finché non salvi una nuova formazione.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              data-testid="reset-team-cancel"
              className="bg-transparent border-[#3F3F4C] text-gray-300 hover:bg-[#242429] hover:text-white"
            >
              Annulla
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="reset-team-confirm"
              onClick={resetTeam}
              className="bg-[#EF4444] hover:bg-[#DC2626] text-white font-bold"
            >
              Sì, azzera tutto
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <div
        data-testid="team-dock"
        className="fixed bottom-[68px] lg:bottom-0 left-0 right-0 z-40 bg-[#1A1A1E]/95 backdrop-blur-xl border-t border-[#3F3F4C]"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
          <div className="flex items-center gap-3 shrink-0">
            <Coins className="w-5 h-5 text-[#FFB703]" />
            <div className="leading-none">
              <p className="font-body-sport text-[9px] tracking-widest text-gray-500 uppercase">
                Crediti residui
              </p>
              <p
                data-testid="credits-counter"
                className={`font-time font-bold text-xl ${overBudget ? "text-blink-red" : "text-[#FFB703]"}`}
              >
                {residui} <span className="text-xs text-gray-500">/ spesi {costo}</span>
              </p>
            </div>
          </div>

          <div className="leading-none shrink-0">
            <p className="font-body-sport text-[9px] tracking-widest text-gray-500 uppercase">
              Piloti selezionati
            </p>
            <p
              data-testid="pilots-counter"
              className={`font-time font-bold text-xl ${selected.length === TEAM_SIZE ? "text-[#22C55E]" : "text-white"}`}
            >
              {selected.length} <span className="text-xs text-gray-500">/ {TEAM_SIZE}</span>
            </p>
          </div>

          <div className="flex-1 flex items-center gap-1.5 overflow-x-auto py-1">
            {Array.from({ length: TEAM_SIZE }).map((_, i) => {
              const pid = selected[i];
              const p = pid ? byId[pid] : null;
              return (
                <span key={pid || `empty-${i}`} data-testid={`team-slot-${i}`} className="contents">
                  {p ? (
                    <button
                      data-testid={`dock-pilot-${p.id}`}
                      onClick={() => setCaptain(p)}
                      title="Tocca per nominare Capitano"
                      className={`shrink-0 flex items-center gap-1 rounded-full border px-2.5 py-1 font-body-sport text-[11px] font-bold transition-colors ${
                        captainId === p.id
                          ? "border-[#FFB703] bg-[#FFB703]/15 text-[#FFB703]"
                          : "border-[#3F3F4C] text-gray-300 hover:border-[#FF1E27]"
                      }`}
                    >
                      <Star className={`w-3 h-3 ${captainId === p.id ? "fill-[#FFB703]" : "opacity-40"}`} />
                      {p.cognome}
                    </button>
                  ) : (
                    <span className="shrink-0 rounded-full border border-dashed border-[#3F3F4C] px-3 py-1 text-[11px] text-gray-600 font-body-sport">
                      Slot {i + 1}
                    </span>
                  )}
                </span>
              );
            })}
          </div>

          <div className="flex items-center gap-2 shrink-0">
            <input
              data-testid="team-name-input"
              className={`${inputCls} w-40 hidden sm:block`}
              placeholder="Nome scuderia"
              value={nomeSquadra}
              onChange={(e) => setNomeSquadra(e.target.value)}
            />
            <button
              data-testid="save-team-button"
              onClick={saveTeam}
              disabled={!canSubmit}
              className="flex items-center gap-2 rounded-lg bg-[var(--brand-primary)] hover:bg-[#CC1118] disabled:opacity-40 disabled:cursor-not-allowed transition-colors px-5 py-2.5 min-h-[48px] font-racing font-bold text-xs tracking-widest text-white uppercase"
            >
              <Save className="w-4 h-4" />
              Salva Squadra ({selected.length}/{TEAM_SIZE})
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
